import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { useI18n } from "../i18n";

const HERO_IMG =
  "https://images.unsplash.com/photo-1607082615064-eb85a8c1f4cd?fm=jpg&q=80&w=2400&auto=format&fit=crop";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.4 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = ({ onQuote }) => {
  const { t } = useI18n();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex items-end pb-20 md:pb-28"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Sports car neon blue" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/70" />
        <div className="absolute inset-0 radial-cyan" />
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="font-mono-accent text-xs uppercase tracking-[0.3em] text-cyan mb-6"
        >
          {t("hero.tag")}
        </motion.div>

        <motion.div
          data-testid="prelaunch-hero-banner"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6 }}
          className="inline-flex items-center gap-3 mb-6 rounded-full border border-cyan-400/40 bg-cyan-400/5 backdrop-blur px-4 py-2"
        >
          <span className="relative inline-flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
          </span>
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-cyan">
            {t("preLaunch.badge")} — Saint-Martin
          </span>
        </motion.div>

        <h1 className="font-display font-900 uppercase leading-[0.85] tracking-tighter text-[16vw] md:text-[13vw] lg:text-[11rem]">
          <span className="line-mask">
            <motion.span variants={line} initial="hidden" animate="show" custom={0} className="block">
              Wrapp
            </motion.span>
          </span>
          <span className="line-mask">
            <motion.span variants={line} initial="hidden" animate="show" custom={1} className="block text-cyan neon-text-glow">
              Man
            </motion.span>
          </span>
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="max-w-md text-lg text-neutral-300 font-light"
          >
            {t("hero.subtitle")}
            <span data-testid="prelaunch-hero-notice" className="block mt-4 text-sm text-neutral-400 border-l-2 border-cyan-400/50 pl-4">
              {t("preLaunch.heroBanner")}
            </span>
          </motion.p>

          <motion.button
            data-testid="hero-cta-devis"
            onClick={onQuote}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="group inline-flex items-center gap-4 text-black font-medium text-base px-8 py-4 rounded-full neon-glow hover:scale-[1.03] active:scale-95 transition-transform duration-300"
            style={{ backgroundColor: "#C9A96A" }}
          >
            {t("hero.cta")}
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/15 group-hover:translate-x-1 transition-transform duration-300">
              →
            </span>
          </motion.button>
        </div>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-neutral-500">
        <ArrowDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
